/**
 * Drupal's `webform_rest` rejects a submission with a 400 whose body explains
 * exactly what was wrong — per-field messages, or a form-level refusal. These
 * helpers parse that body into something we can log and show the submitter.
 */

import { z } from 'zod'

/** Per-element validation messages, keyed by the Drupal element key. */
export type CmsValidationFields = Record<string, string>

export type CmsFormError =
  /** The submission was rejected field by field. */
  | { kind: 'validation'; fields: CmsValidationFields }
  /** The form itself refused it — closed, permission denied, no data. */
  | { kind: 'form'; message: string }

// Drupal sends a top-level `message` with a per-field rejection and omits it for
// a form-level refusal. That difference is the only reliable way to tell the two
// apart, since a refusal reports itself as a field literally named `message`.
// Values are `unknown` so one odd entry doesn't discard the rest of the map.
const envelopeSchema = z.object({
  message: z.string().optional(),
  error: z.record(z.string(), z.unknown()),
})

/**
 * Reads a rejected `webform_rest/submit` body.
 *
 * Returns null for anything that isn't a recognised Drupal error envelope, so
 * an unexpected upstream response stays generic instead of being forwarded to
 * the browser as if we understood it.
 */
export function parseCmsFormError(body: unknown): CmsFormError | null {
  const parsed = envelopeSchema.safeParse(body)
  if (!parsed.success) return null

  const entries = Object.entries(parsed.data.error).filter(
    (entry): entry is [string, string] => typeof entry[1] === 'string' && entry[1] !== '',
  )
  if (!entries.length) return null

  if (parsed.data.message === undefined) {
    // No top-level message, so this is a refusal rather than a rejection. Only
    // Drupal's exact shape — a lone `error.message` — is forwarded; a passing
    // proxy or WAF that happens to answer 400 with JSON is not ours to relay.
    const only = entries.length === 1 ? entries[0] : undefined
    return only && only[0] === 'message' ? { kind: 'form', message: only[1] } : null
  }

  return { kind: 'validation', fields: Object.fromEntries(entries) }
}

/**
 * The message to show for a failed form POST. `data.message` wins because h3
 * strips non-ASCII from `statusMessage`, which would leave a Swedish message
 * missing its letters; `statusMessage` remains the fallback for the error paths
 * that don't set one.
 */
export function formErrorMessage(response: { statusMessage?: unknown; data?: unknown }): string {
  const data = response?.data
  const fromData =
    data && typeof data === 'object' && typeof (data as { message?: unknown }).message === 'string'
      ? (data as { message: string }).message
      : ''
  return fromData || (typeof response?.statusMessage === 'string' ? response.statusMessage : '')
}

const MAX_KEYS = 20

/**
 * Describes the *shape* of a rejection body we could not parse, for the error
 * report. Values are deliberately never included: an unrecognised body may echo
 * the submission back, and these forms carry personnummer. Shape plus the HTTP
 * status is enough to tell a proxy page from a changed Drupal envelope.
 */
export function describeCmsBody(body: unknown): string {
  if (body === null || body === undefined) return String(body)
  if (typeof body === 'string') return `string(${body.length} chars)`
  if (Array.isArray(body)) return `array(${body.length})`
  if (typeof body === 'object') {
    const keys = Object.keys(body)
    const shown = keys.slice(0, MAX_KEYS).sort().join(',')
    return `object{${shown}${keys.length > MAX_KEYS ? ',…' : ''}}`
  }
  return typeof body
}

/**
 * The text to show the submitter. Drupal's per-field messages are written for
 * exactly this purpose (`#pattern_error` and friends are editor-authored), so
 * they are passed through rather than remapped.
 */
export function cmsFormErrorMessage(error: CmsFormError): string {
  return error.kind === 'form' ? error.message : Object.values(error.fields).join(' ')
}
